import { useId, useState, type FormEvent } from 'react';
import {
  AuthError,
  completeNewPassword,
  confirmPasswordReset,
  isConfigured,
  requestPasswordReset,
  signIn,
  storeTokens,
  submitMfaCode,
  type Tokens,
} from './auth.js';
import { beginGoogleSignIn, isFederationConfigured } from './oidc.js';

/**
 * Sign-in (Epic 1).
 *
 * Three stages rather than three screens: the password form, and the two
 * challenges Cognito can answer with. An administratively created account always
 * arrives at the second one on its first use (bootstrap §7), so it is a normal
 * path and not an error state.
 *
 * Phase 3 restyles this; what matters now is that it is operable. Every field has
 * a real label, errors are announced rather than only colored (NFR-UX-3), and
 * the messages say what to do next without mentioning status codes (NFR-UX-8).
 */
type Stage =
  | { readonly kind: 'password' }
  | { readonly kind: 'new-password'; readonly session: string; readonly username: string }
  | { readonly kind: 'mfa'; readonly session: string; readonly username: string }
  /** Asking which account to reset (FR-1.4). */
  | { readonly kind: 'forgot' }
  /** Holding the emailed code, and where Cognito said it went. */
  | { readonly kind: 'reset'; readonly sentTo: string | null };

/** The two stages that are a reset rather than a sign-in attempt. */
const isReset = (stage: Stage): boolean => stage.kind === 'forgot' || stage.kind === 'reset';

export function SignIn({
  onSignedIn,
  onBack,
}: {
  onSignedIn: (tokens: Tokens) => void;
  /**
   * Leaving without signing in.
   *
   * Owned by this component rather than rendered beside it, because it belongs
   * in the same row as the submit button: two sibling blocks, each with its own
   * button, stack into a column of mismatched widths with nothing relating
   * them. Offered at every stage -- a challenge you cannot escape is a trap,
   * and the cost of leaving one is signing in again.
   */
  onBack?: () => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'password' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Something that went right, which an error region cannot carry.
   *
   * A reset ends by returning to the sign-in form, and a form that simply
   * reappears looks like a failure. Kept apart from `error` so neither can
   * overwrite the other into a half-sentence.
   */
  const [notice, setNotice] = useState<string | null>(null);

  const errorId = useId();
  const noticeId = useId();
  const resetCodeId = useId();
  const emailId = useId();
  const passwordId = useId();
  const newPasswordId = useId();
  const codeId = useId();

  if (!isConfigured()) {
    /*
     * Still offers the way out.
     *
     * This branch renders instead of the form, so a `Back` that lived only
     * beside the submit button would leave the one screen that cannot do
     * anything as the one screen you cannot leave.
     */
    return (
      <>
        <p className="warn" role="status">
          Sign-in is not configured for this build.
        </p>
        {onBack !== undefined && (
          <div className="actions">
            <button type="button" onClick={onBack}>
              Back
            </button>
          </div>
        )}
      </>
    );
  }

  /** One place for the outcome, so each stage cannot interpret it differently. */
  async function attempt(action: () => Promise<Awaited<ReturnType<typeof signIn>>>) {
    setBusy(true);
    setError(null);
    try {
      const result = await action();
      if (result.status === 'signed-in') {
        storeTokens(result.tokens);
        onSignedIn(result.tokens);
        return;
      }
      if (result.status === 'new-password-required') {
        setStage({ kind: 'new-password', session: result.session, username: result.username });
        return;
      }
      setStage({ kind: 'mfa', session: result.session, username: result.username });
    } catch (caught) {
      // AuthError messages are already written for a person to read; anything
      // else must not leak an exception string into the page.
      setError(caught instanceof AuthError ? caught.message : 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * The same sequencing as `attempt`, for operations that end in a stage rather
   * than in a session. Separate because a reset returns nothing to interpret,
   * and threading it through `attempt` would mean a result type that is mostly
   * absent.
   */
  async function perform(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(
        caught instanceof AuthError ? caught.message : 'That did not work. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  /** Into the reset flow, keeping the address already typed. */
  const backToForgot = () => {
    setStage({ kind: 'forgot' });
    setPassword('');
    setError(null);
    setNotice(null);
  };

  /** Back to the start of the form, keeping the address already typed. */
  const backToSignIn = (said: string | null = null) => {
    setStage({ kind: 'password' });
    setPassword('');
    setNewPassword('');
    setCode('');
    setError(null);
    setNotice(said);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (stage.kind === 'forgot') {
      void perform(async () => {
        const sentTo = await requestPasswordReset(email);
        setNotice(null);
        setStage({ kind: 'reset', sentTo });
      });
    } else if (stage.kind === 'reset') {
      void perform(async () => {
        await confirmPasswordReset({ email, code, newPassword });
        backToSignIn('Password changed. Sign in with it below.');
      });
    } else if (stage.kind === 'password') {
      void attempt(() => signIn(email, password));
    } else if (stage.kind === 'new-password') {
      void attempt(() =>
        completeNewPassword({
          username: stage.username,
          session: stage.session,
          newPassword,
        }),
      );
    } else {
      void attempt(() => submitMfaCode({ username: stage.username, session: stage.session, code }));
    }
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      {/*
        Rendered only when there is something to say, unlike the error region
        below it -- that one reserves its line so an arriving error cannot shift
        the button under the pointer, whereas this one only ever appears in
        response to a press that has already finished.
      */}
      {notice !== null && (
        <p id={noticeId} className="muted" role="status">
          {notice}
        </p>
      )}

      {stage.kind === 'password' && isFederationConfigured() && (
        <div className="actions" style={{ marginBottom: '1rem' }}>
          {/*
            Above the password form, because it is the shorter path for
            someone who has linked their Google account. The password form
            stays: an account reachable through one provider only is one
            outage away from being unreachable.
          */}
          <button
            type="button"
            onClick={() => {
              void beginGoogleSignIn().catch((err: unknown) =>
                setError(err instanceof Error ? err.message : 'Could not start Google sign-in'),
              );
            }}
            disabled={busy}
          >
            Continue with Google
          </button>
        </div>
      )}

      {stage.kind === 'password' && (
        <>
          <h2>Sign in</h2>
          <div className="field">
            <label htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>
          <div className="field">
            <label htmlFor={passwordId}>Password</label>
            <input
              id={passwordId}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>
        </>
      )}

      {stage.kind === 'new-password' && (
        <>
          <h2>Choose a password</h2>
          <p className="muted">
            This account was created with a temporary password. Pick a new one to continue.
          </p>
          <div className="field">
            <label htmlFor={newPasswordId}>New password</label>
            <input
              id={newPasswordId}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={busy}
              aria-describedby={error === null ? `${newPasswordId}-hint` : errorId}
            />
            {/* States the policy up front rather than only on rejection. */}
            <p id={`${newPasswordId}-hint`} className="muted hint">
              At least 12 characters, with upper and lower case, a number and a symbol.
            </p>
          </div>
        </>
      )}

      {stage.kind === 'mfa' && (
        <>
          <h2>Enter your code</h2>
          <div className="field">
            <label htmlFor={codeId}>Authenticator code</label>
            <input
              id={codeId}
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>
        </>
      )}

      {stage.kind === 'forgot' && (
        <>
          <h2>Reset your password</h2>
          <p className="muted">
            We will email you a code. Enter it on the next screen with a new password.
          </p>
          <div className="field">
            <label htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>
        </>
      )}

      {stage.kind === 'reset' && (
        <>
          <h2>Enter your code</h2>
          {/*
            Phrased as a conditional even though a code was just requested.
            `PreventUserExistenceErrors` makes the request succeed for an address
            with no account, so stating that a code was sent would be the one
            sentence that tells an attacker which addresses are registered
            (NFR-SEC-6). The masked destination comes from Cognito and is
            fabricated in that case, which is the mechanism working.
          */}
          <p className="muted">
            {stage.sentTo === null
              ? 'If that address has an account, a code is on its way.'
              : `If that address has an account, a code is on its way to ${stage.sentTo}.`}
          </p>
          <div className="field">
            <label htmlFor={resetCodeId}>Code</label>
            <input
              id={resetCodeId}
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              required
              disabled={busy}
              {...(error === null ? {} : { 'aria-describedby': errorId })}
            />
          </div>
          <div className="field">
            <label htmlFor={newPasswordId}>New password</label>
            <input
              id={newPasswordId}
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={busy}
              aria-describedby={error === null ? `${newPasswordId}-hint` : errorId}
            />
            {/* The same policy as the forced first change, stated up front. */}
            <p id={`${newPasswordId}-hint`} className="muted hint">
              At least 12 characters, with upper and lower case, a number and a symbol.
            </p>
          </div>
        </>
      )}

      {/*
        Announced on change, so the failure is not conveyed by color alone.

        Mounted empty and filled later, because a live region inserted already
        holding its message is not reliably announced -- assistive technology
        watches regions it already knows about. The blank line it leaves is
        deliberate too: `[role='alert']` reserves it, so an error appearing does
        not shift the button out from under the pointer or the focus ring that
        is on it at exactly that moment.

        The text goes directly in this element rather than in a child, so that
        the reservation matches what fills it. A nested paragraph would bring
        its own margins and overflow the reserved line, reintroducing the shift
        in the one case it exists to prevent.
      */}
      <p id={errorId} className="warn" role="alert" aria-live="polite">
        {error ?? ''}
      </p>

      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          {busy ? 'Working…' : submitLabel(stage)}
        </button>

        {/*
          Only from the form itself, where it is the only way anyone has asked
          for a reset. Offering it mid-reset would be a third button competing
          with the one that finishes the thing already started.
        */}
        {stage.kind === 'password' && (
          <button type="button" onClick={() => backToForgot()} disabled={busy}>
            Forgot your password?
          </button>
        )}

        {/*
          Inside a reset, back means back a step rather than out of the app: the
          reset was reached from this form and returns to it. Elsewhere it is the
          caller's way out, offered at every stage because a challenge you cannot
          leave is a trap.
        */}
        {isReset(stage) ? (
          <button type="button" onClick={() => backToSignIn()} disabled={busy}>
            Back to sign in
          </button>
        ) : (
          onBack !== undefined && (
            <button type="button" onClick={onBack} disabled={busy}>
              Back
            </button>
          )
        )}
      </div>
    </form>
  );
}

/** What the primary button does, which differs at every stage. */
function submitLabel(stage: Stage): string {
  switch (stage.kind) {
    case 'password':
      return 'Sign in';
    case 'forgot':
      return 'Send a code';
    case 'reset':
      return 'Set password';
    default:
      return 'Continue';
  }
}
